import React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, KeyRound, Tag, UserMinus, Users, UsersRound } from 'lucide-react'
import { sdk } from '@/lib/sdk'
import { cn } from '@/lib/utils'
import { MemberAutocomplete } from '@/components/ui/member-autocomplete'
import { MAX_AUDIENCE_FIELD_CONDITIONS, type AudienceFieldCondition, type AudienceFieldWarning } from '@/types/campaigns'
import { AudienceFieldConditions } from './AudienceFieldConditions'
import { useAudienceFields } from './audienceFields'

export interface PickedPerson {
  id: string
  name: string
  role: string
  type: string
  avatar?: string
}

/** Groups, tags and chosen people: one side of the audience (who is in, or who is left out). */
export interface AudienceCriteria {
  groupIds: string[]
  tags: string[]
  people: PickedPerson[]
  /** F-156: conditions on custom fields (campaigns only, `fields` below). */
  fields?: AudienceFieldCondition[]
}

export interface AudienceValue extends AudienceCriteria {
  all: boolean
  /** F-175: "who can open this item", a mode of its own (never with `all` or the inclusion lists). */
  itemAccess?: { type: string; id: string } | null
  exclude: AudienceCriteria
}

/** F-175: the item a campaign can be sent to the readers of, with what the live preview says about it. */
export interface AudienceItemOption {
  type: string
  id: string
  /** Null until the preview (or the item itself) has answered. */
  title: string | null
  /** The preview's count while this mode is on; null otherwise. */
  count: number | null
  /** The item was deleted or is no longer published: nobody would receive the e-mail. */
  unavailable?: boolean
}

interface AudiencePickerProps {
  value: AudienceValue
  onChange: (value: AudienceValue) => void
  disabled?: boolean
  error?: string
  /** A server error on `audience.exclude.*`, shown under the exclusion. */
  excludeError?: string
  /** Surveys (F-103) have no exclusion: their audience is a plain union. */
  showExclude?: boolean
  /** F-175: offers a third mode, "who can open {title}" (campaigns made from a post). */
  item?: AudienceItemOption | null
  /** F-156: offers "Campos" (conditions on custom fields) in the inclusion and in the exclusion. */
  fields?: boolean
  /** F-156: the field conditions that no longer pick whom they picked (the preview's, or the saved draft's). */
  fieldWarnings?: AudienceFieldWarning[]
  /** F-156: the fields already read by the caller (it needs them to build the request); read here otherwise. */
  fieldOptions?: ReturnType<typeof useAudienceFields>
}

const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v])

const chip = (active: boolean, disabled?: boolean) =>
  cn(
    'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
    active ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    disabled && 'pointer-events-none opacity-60'
  )

interface CriteriaFieldsProps {
  value: AudienceCriteria
  onChange: (value: AudienceCriteria) => void
  groups: { id: string; name: string }[]
  tagOptions: { tag: string; count: number }[]
  noAccess: boolean
  disabled?: boolean
  /** Prefix for the headings' ids, so the two sides stay distinct for screen readers. */
  idPrefix: string
  /** False hides the people picker (F-176's host rule chooses groups and tags only). */
  showPeople?: boolean
}

/** The community's groups and tags for these selectors (owner and admin; `noAccess` on 403). */
export const useAudienceOptions = () => {
  const [groups, setGroups] = React.useState<{ id: string; name: string }[]>([])
  const [noAccess, setNoAccess] = React.useState(false)
  const [tagOptions, setTagOptions] = React.useState<{ tag: string; count: number }[]>([])
  React.useEffect(() => {
    sdk.memberGroups.list().then((list) => setGroups(Array.isArray(list) ? list : [])).catch((e) => { setGroups([]); if (e?.response?.status === 403) setNoAccess(true) })
    sdk.members.listTags().then(setTagOptions).catch((e) => { setTagOptions([]); if (e?.response?.status === 403) setNoAccess(true) })
  }, [])
  return { groups, tagOptions, noAccess }
}

/** The three selectors (groups, tags, people), used for both inclusion and exclusion (and, without people, by F-176). */
export const CriteriaFields = ({ value, onChange, groups, tagOptions, noAccess, disabled, idPrefix, showPeople = true }: CriteriaFieldsProps) => {
  const { t } = useTranslation('campaigns')
  return (
    <div className="space-y-5">
      <div className="space-y-2" role="group" aria-labelledby={`${idPrefix}-groups`}>
        <h4 id={`${idPrefix}-groups`} className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <UsersRound className="h-4 w-4 text-gray-400" />
          {t('audience.groups')}
        </h4>
        {groups.length === 0 ? <p className="text-sm text-gray-500">{noAccess ? t('audience.noAccess') : t('audience.noGroups')}</p> : (
          <div className="flex flex-wrap gap-2">
            {groups.map((g) => {
              const on = value.groupIds.includes(g.id)
              return (
                <button key={g.id} type="button" aria-pressed={on} className={chip(on, disabled)}
                  onClick={() => onChange({ ...value, groupIds: toggle(value.groupIds, g.id) })}>
                  {on && <Check className="h-3.5 w-3.5" />}
                  {g.name}
                </button>
              )
            })}
          </div>
        )}
      </div>

      <div className="space-y-2" role="group" aria-labelledby={`${idPrefix}-tags`}>
        <h4 id={`${idPrefix}-tags`} className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Tag className="h-4 w-4 text-gray-400" />
          {t('audience.tags')}
        </h4>
        {tagOptions.length === 0 ? <p className="text-sm text-gray-500">{noAccess ? t('audience.noAccess') : t('audience.noTags')}</p> : (
          <div className="flex flex-wrap gap-2">
            {tagOptions.map((x) => {
              const on = value.tags.includes(x.tag)
              return (
                <button key={x.tag} type="button" aria-pressed={on} className={chip(on, disabled)}
                  onClick={() => onChange({ ...value, tags: toggle(value.tags, x.tag) })}>
                  {on && <Check className="h-3.5 w-3.5" />}
                  {t('audience.tagOption', { tag: x.tag, count: x.count })}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {showPeople && <div className="space-y-2">
        <h4 className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Users className="h-4 w-4 text-gray-400" />
          {t('audience.people')}
        </h4>
        <MemberAutocomplete
          selectedMembers={value.people}
          onMembersChange={(list) => onChange({ ...value, people: list as PickedPerson[] })}
          onSearch={async (search) => ((await sdk.members.autocomplete(search)) as PickedPerson[]) || []}
          placeholder={t('audience.peoplePlaceholder')}
          disabled={disabled}
        />
      </div>}
    </div>
  )
}

/** Everyone, or a union of groups, tags and chosen people; minus anyone matching the exclusion. */
export const AudiencePicker = ({ value, onChange, disabled, error, excludeError, showExclude = true, item, fields: withFields = false, fieldWarnings = [], fieldOptions: givenFields }: AudiencePickerProps) => {
  const { t } = useTranslation('campaigns')
  const { groups, tagOptions, noAccess } = useAudienceOptions()
  const ownFields = useAudienceFields(withFields && !givenFields)
  const fieldOptions = givenFields ?? ownFields
  /* The limit counts both sides together; a field inclusion never goes with item access (the API refuses it). */
  const includeFields = value.fields ?? []
  const excludeFields = value.exclude.fields ?? []
  const room = MAX_AUDIENCE_FIELD_CONDITIONS - includeFields.length - excludeFields.length

  const current: 'all' | 'choose' | 'item' = value.itemAccess ? 'item' : value.all ? 'all' : 'choose'
  const mode = (m: 'all' | 'choose' | 'item') => cn(
    'min-w-0 flex-1 rounded-lg border px-4 py-2.5 text-left text-sm transition-colors',
    m === current ? 'border-primary bg-primary-50 text-primary-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
    disabled && 'pointer-events-none opacity-60'
  )
  const itemTitle = item?.title ? t('audience.itemAccess.title', { title: item.title }) : t('audience.itemAccess.titleUnknown')
  const shared = { groups, tagOptions, noAccess, disabled }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap" role="radiogroup" aria-label={t('audience.title')}>
        {item && (
          <button type="button" role="radio" aria-checked={current === 'item'} className={cn(mode('item'), 'sm:basis-full')} data-testid="audience-item-access"
            onClick={() => onChange({ ...value, all: false, itemAccess: { type: item.type, id: item.id } })}>
            <span className="flex items-start gap-1.5 font-medium">
              <KeyRound className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              <span className="min-w-0 break-words">{itemTitle}</span>
            </span>
            <span className="block text-xs text-gray-500">
              {current === 'item' && item.count !== null
                ? t('audience.itemAccess.count', { count: item.count })
                : t('audience.itemAccess.hint')}
            </span>
          </button>
        )}
        <button type="button" role="radio" aria-checked={current === 'all'} className={mode('all')} onClick={() => onChange({ ...value, all: true, itemAccess: null })}>
          <span className="block font-medium">{t('audience.everyone')}</span>
          <span className="block text-xs text-gray-500">{t('audience.everyoneHint')}</span>
        </button>
        <button type="button" role="radio" aria-checked={current === 'choose'} className={mode('choose')} onClick={() => onChange({ ...value, all: false, itemAccess: null })}>
          <span className="block font-medium">{t('audience.choose')}</span>
          <span className="block text-xs text-gray-500">{withFields ? t('audience.chooseHintFields') : t('audience.chooseHint')}</span>
        </button>
      </div>

      {current === 'item' && (
        <p className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600" data-testid="audience-item-access-note">
          {item?.unavailable ? t('audience.itemAccess.unavailable') : t('audience.itemAccess.note')}
        </p>
      )}

      {current === 'choose' && (
        <div className="space-y-3">
          <CriteriaFields {...shared} idPrefix="audience-include" value={value}
            onChange={(c) => onChange({ ...value, groupIds: c.groupIds, tags: c.tags, people: c.people })} />
          {withFields && (
            <AudienceFieldConditions idPrefix="audience-include" value={includeFields} fields={fieldOptions} room={room} disabled={disabled}
              warnings={fieldWarnings.filter((w) => w.scope === 'include')}
              onChange={(list) => onChange({ ...value, fields: list })} />
          )}
          <p className="text-xs text-gray-500">{t('audience.unionHint')}</p>
        </div>
      )}
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}

      {showExclude && <div className={cn('space-y-3 rounded-lg border p-4', excludeError ? 'border-red-300' : 'border-gray-200')}
        role="group" aria-labelledby="audience-exclude-title">
        <div>
          <h4 id="audience-exclude-title" className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <UserMinus className="h-4 w-4 text-gray-400" />
            {t('audience.exclude.title')}
          </h4>
          <p className="mt-1 text-xs text-gray-500">{withFields ? t('audience.exclude.hintFields') : t('audience.exclude.hint')}</p>
        </div>
        <CriteriaFields {...shared} idPrefix="audience-exclude" value={value.exclude}
          onChange={(c) => onChange({ ...value, exclude: { ...value.exclude, groupIds: c.groupIds, tags: c.tags, people: c.people } })} />
        {withFields && (
          <AudienceFieldConditions idPrefix="audience-exclude" value={excludeFields} fields={fieldOptions} room={room} disabled={disabled}
            warnings={fieldWarnings.filter((w) => w.scope === 'exclude')}
            onChange={(list) => onChange({ ...value, exclude: { ...value.exclude, fields: list } })} />
        )}
        {excludeError && <p className="text-sm text-red-600" role="alert">{excludeError}</p>}
      </div>}
    </div>
  )
}
